// Pure, testable status-resolution logic for the match lifecycle.
// Used by saveMatchToDb to decide what status and is_public to write.

export interface StatusResolution {
  status: string;
  isPublic: boolean;
}

/**
 * Given the raw MatchState fields, determine the canonical DB status and
 * whether the match should be publicly visible.
 *
 * Rules:
 *  - A match with legal deliveries bowled is always at least "live".
 *  - Legacy / variant status strings are normalized to lowercase.
 *  - Draft matches are private. Scheduled, live, upcoming and completed
 *    matches are public.
 *  - "deleted" keeps its existing is_public unchanged.
 */
const RANK: Record<string, number> = {
  draft: 0,
  scheduled: 1,
  upcoming: 1,
  live: 2,
  completed: 3,
  ended: 3,
};

export function isStatusRegression(
  currentDbStatus: string | undefined | null,
  proposedStatus: string
): boolean {
  const current = (currentDbStatus || "").toLowerCase();
  const proposed = (proposedStatus || "").toLowerCase();

  if (proposed === "abandoned" || proposed === "deleted") return false;
  if (current === "abandoned" || current === "deleted") return false;

  const oldRank = RANK[current] ?? 0;
  const newRank = RANK[proposed] ?? 0;

  return newRank < oldRank;
}

export function resolveMatchLifecycle(
  rawStatus: string | undefined | null,
  hasActivity: boolean,
  hasToss: boolean,
  currentIsPublic: boolean
): StatusResolution {
  let status = rawStatus || "";

  // Normalize legacy / variant status values
  if (!status) {
    if (hasActivity) status = "live";
    else status = "draft";
  } else if (status === "Upcoming") {
    status = "scheduled";
  } else if (status === "In Progress") {
    status = "live";
  } else if (status === "Completed" || status === "ended") {
    status = "completed";
  }

  // Safety net: a match with scored deliveries or a completed toss must never
  // stay "draft"/"scheduled" — the toss makes the match live for public viewers.
  if ((status === "draft" || status === "scheduled") && (hasActivity || hasToss)) {
    status = "live";
  }

  // Derive public visibility from status
  // Draft matches are private. Scheduled, live, upcoming and completed are public.
  let isPublic = currentIsPublic;
  if (
    status === "live" ||
    status === "scheduled" ||
    status === "upcoming" ||
    status === "completed"
  ) {
    isPublic = true;
  } else if (status === "draft") {
    isPublic = false;
  } else if (status === "deleted") {
    // preserve existing flag
  }

  return { status, isPublic };
}

export function resolveStatusWithGuard(
  rawStatus: string | undefined | null,
  hasActivity: boolean,
  hasToss: boolean,
  currentIsPublic: boolean,
  currentDbStatus: string | undefined | null
): StatusResolution {
  const resolved = resolveMatchLifecycle(rawStatus, hasActivity, hasToss, currentIsPublic);

  if (isStatusRegression(currentDbStatus, resolved.status)) {
    const dbStatus = (currentDbStatus || "draft").toLowerCase();
    const isPublic =
      dbStatus === "live" ||
      dbStatus === "scheduled" ||
      dbStatus === "upcoming" ||
      dbStatus === "completed";
    return { status: dbStatus, isPublic };
  }

  return resolved;
}
